import { io } from 'socket.io-client';
import { EventEmitter } from 'events';

// Stable per-browser-tab identifier, used so the server can tell "this is the
// same tab reconnecting" apart from "this is a genuinely different device/tab"
// when enforcing session limits. sessionStorage is scoped per tab and survives
// reconnects/reloads of that same tab, but a new tab/window gets its own.
const TAB_ID_KEY = 'clusterden_tab_id';
const getTabId = () => {
  try {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
    return tabId;
  } catch {
    return null; // sessionStorage unavailable (e.g. privacy mode) — falls back to old per-connection eviction behavior
  }
};

class ConnectionMonitor extends EventEmitter {
  constructor() {
    super();
    this.socket = null;
    this.isConnected = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 2; // Reduced to 2 attempts for faster recovery
    this.reconnectDelay = 800; // 0.8 seconds base delay (fast but safe)
    this.connectionTimeout = 3000; // 3 seconds timeout per attempt (very fast failure detection)
    this.reconnectTimeout = null;
    this.connectionTimeoutId = null; // For tracking connection attempt timeout
    this.diagnosedIssueType = null; // Store diagnosed issue type to avoid repeated health checks
    this.originalUserId = null;
    this.originalDispatch = null;
    this.initialConnectionTimeout = null; // For first-time connection timeout

    // Event handler references
    this.receiveMessageHandler = null;
    this.messageStatusUpdateHandler = null;
    this.newInboxMessageHandler = null;
    this.assignmentCountChangedHandler = null;
    this.userConnectedUpdateHandler = null;
    this.userDisconnectedUpdateHandler = null;
    this.contactLabelsUpdatedHandler = null;
    this.forcedDisconnectHandler = null;
    this.connectionWarningHandler = null;
    this.messageReactionHandler = null;
    this.teamMemberChatMessageHandler = null;
    this.teamMemberChatStatusUpdateHandler = null;
    this.teamMemberChatInboxUpdateHandler = null;
  }

  // Initialize socket connection
  initializeSocket(userId, dispatchFn, accessToken = null) {
    this.originalUserId = userId;
    this.originalDispatch = dispatchFn;
    
    // WhatsApp-style: Ensure dispatch is ready BEFORE connecting socket
    // This prevents race conditions where events arrive before Redux is ready
    if (!dispatchFn || typeof dispatchFn !== 'function') {
      // Try to get dispatch from store as fallback
      const { store } = require('../redux/store');
      if (store && store.dispatch && typeof store.dispatch === 'function') {
        this.originalDispatch = store.dispatch;
      } else {
        // Wait a bit and retry (dispatch might be initializing)
        setTimeout(() => {
          this.initializeSocket(userId, dispatchFn, accessToken);
        }, 100);
        return;
      }
    }
    
    // Disconnect existing socket if any
    if (this.socket) {
      this.socket.disconnect();
    }

    // Get access token from tokenManager if not provided
    if (!accessToken) {
      const { getAccessToken } = require('./tokenManager');
      accessToken = getAccessToken();
    }

    // Log connection initialization
    console.log('🔧 Client: Initializing socket connection for user:', userId);
    console.log('⚡ Ultra-fast reconnection settings:', {
      maxAttempts: this.maxReconnectAttempts,
      baseDelay: `${this.reconnectDelay/1000}s`,
      connectionTimeout: `${this.connectionTimeout/1000}s`,
      totalMaxTime: `${(this.reconnectDelay * this.maxReconnectAttempts + this.connectionTimeout * this.maxReconnectAttempts)/1000}s`,
      timing: '0.8s, 1.6s delays with 3s timeout per attempt'
    });

    // Get token from memory (tokenManager) - not from localStorage for security
    // If accessToken was not provided, getAccessToken() was already called above
    const token = accessToken || null;
    
    // Get workspaceId from Redux state (not localStorage for security)
    let workspaceId = null;
    try {
      const { store } = require('../redux/store');
      const state = store.getState();
      workspaceId = state.auth?.user?.workspaceId || null;
    } catch (error) {
      console.warn('Failed to get workspaceId from Redux:', error);
    }

    // Create new socket with JWT authentication.
    // Sockets now connect to the dedicated socket-server, not the API server —
    // REACT_APP_WS_URL takes priority; falls back to REACT_APP_SERVER_DOMAIN
    // (the old combined-server behavior) if unset in a given environment.
    const serverDomain = process.env.REACT_APP_WS_URL || process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

    console.log('🔧 [CONNECTION MONITOR] Creating socket connection:', {
      serverDomain,
      userId,
      hasToken: !!(accessToken || token),
      workspaceId,
      autoConnect: true
    });
    
    this.socket = io(serverDomain, {
      autoConnect: true,
      reconnection: false, // We handle reconnection manually
      timeout: this.connectionTimeout, // Use optimal timeout (3 seconds)
      transports: ['websocket', 'polling'], // Allow fallback to polling
      upgrade: true, // Allow transport upgrades
      rememberUpgrade: true, // Remember successful transport upgrades
      forceNew: false, // Reuse existing connection if possible
      auth: {
        token: accessToken || token || undefined, // Send JWT token for authentication
      },
      query: {
        token: accessToken || token || undefined, // Fallback: also send in query (for older Socket.IO versions)
        workspaceId: workspaceId || undefined
      }
    });

    // CRITICAL: Log socket creation immediately
    console.log('🔧 [CONNECTION MONITOR] Socket created:', {
      socketExists: !!this.socket,
      connected: this.socket?.connected,
      disconnected: this.socket?.disconnected,
      id: this.socket?.id,
      transport: this.socket?.io?.engine?.transport?.name,
      namespace: this.socket?.nsp?.name,
      autoConnect: true
    });

    // Set up event listeners BEFORE checking connection status
    this.setupSocketListeners();
    
    // CRITICAL: Ensure socket actually connects
    // Even with autoConnect: true, sometimes we need to manually trigger connection
    if (!this.socket.connected && !this.socket.disconnected) {
      console.log('🔧 [CONNECTION MONITOR] Socket not connected yet, ensuring connection attempt...');
      // The socket should auto-connect, but let's verify it's trying
      if (this.socket.io && this.socket.io.readyState !== 'opening' && this.socket.io.readyState !== 'open') {
        console.log('🔧 [CONNECTION MONITOR] Socket readyState:', this.socket.io.readyState, '- manually connecting...');
        this.socket.connect();
      }
    }
    
    // Check connection status after a short delay to see if it connected
    setTimeout(() => {
      console.log('🔍 [CONNECTION MONITOR] Socket status check after 500ms:', {
        connected: this.socket?.connected,
        disconnected: this.socket?.disconnected,
        id: this.socket?.id,
        transport: this.socket?.io?.engine?.transport?.name,
        readyState: this.socket?.io?.readyState,
        hasListeners: {
          connect: this.socket?.listeners('connect').length,
          connect_error: this.socket?.listeners('connect_error').length,
          disconnect: this.socket?.listeners('disconnect').length
        }
      });
      
      // If still not connected, attempt reconnection
      if (!this.isConnected && !this.isReconnecting) {
        console.log('⚠️ [CONNECTION MONITOR] Socket not connected after 500ms, starting reconnection');
        this.attemptReconnection();
      }
    }, 500);
  }

  setupSocketListeners() {
    if (!this.socket) return;

    // Connection successful
    this.socket.on('connect', () => {
      console.log('✅ [CONNECTION MONITOR] Socket connected successfully!');
      console.log('✅ [CONNECTION MONITOR] Connection details:', {
        id: this.socket.id,
        connected: this.socket.connected,
        transport: this.socket.io?.engine?.transport?.name,
        namespace: this.socket.nsp?.name,
        userId: this.originalUserId,
        workspaceId: (() => {
          try {
            const { store } = require('../redux/store');
            return store.getState()?.auth?.user?.workspaceId || null;
          } catch {
            return null;
          }
        })()
      });
      
      this.isConnected = true;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.diagnosedIssueType = null; // Reset diagnosed issue type
      
      // Mark that user has successfully connected at least once
      localStorage.setItem('hasEverConnected', 'true');
      
      // Clear any pending reconnect timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      // Clear connection timeout if it exists
      if (this.connectionTimeoutId) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }

      // Clear initial connection timeout
      if (this.initialConnectionTimeout) {
        clearTimeout(this.initialConnectionTimeout);
        this.initialConnectionTimeout = null;
      }

      // Stop server monitoring since we're connected
      this.stopServerMonitoring();

      // CRITICAL: Emit user_connected AFTER socket is connected
      if (this.originalUserId) {
        // Get workspaceId from Redux state (not localStorage for security)
        let workspaceId = null;
        try {
          const { store } = require('../redux/store');
          workspaceId = store.getState()?.auth?.user?.workspaceId || null;
        } catch (error) {
          console.warn('Failed to get workspaceId from Redux:', error);
        }
        const tabId = getTabId();
        console.log('📡 [CONNECTION MONITOR] Emitting user_connected event:', { userId: this.originalUserId, workspaceId, tabId });
        this.socket.emit("user_connected", { userId: this.originalUserId, workspaceId, tabId });
      }

      // Notify listeners
      this.emit('connection');
      
      // Emit global event for components to listen to
      window.dispatchEvent(new CustomEvent('socketConnected'));

      // CRITICAL: Trigger call handler setup on connect
      // This ensures call handlers are set up even if socketHandler hasn't set them up yet
      console.log('🔧 [CONNECTION MONITOR] Socket connected, triggering call handler setup');
      window.dispatchEvent(new CustomEvent('socketConnectedForCallHandlers'));
    });

    // Connection lost
    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnected = false;

      // Notify listeners
      this.emit('disconnection');

      // Only attempt reconnection if it wasn't a manual disconnect
      if (reason !== 'io client disconnect' && !this.isReconnecting) {
        console.log('🔄 Socket disconnected, starting intelligent reconnection');
        this.attemptReconnection();
      }
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      console.error('❌ [CONNECTION MONITOR] Socket connection error:', {
        message: error.message,
        type: error.type,
        description: error.description,
        context: error.context,
        transport: this.socket?.io?.engine?.transport?.name,
        socketId: this.socket?.id,
        connected: this.socket?.connected,
        disconnected: this.socket?.disconnected
      });
      console.error('❌ [CONNECTION MONITOR] Full error object:', error);
      
      this.isConnected = false;
      
      // Notify listeners
      this.emit('disconnection');

      // Don't retry for authentication errors - these need user action or token refresh
      if (error.message && (
        error.message.includes('Authentication token required') ||
        error.message.includes('Token expired') ||
        error.message.includes('Invalid token') ||
        error.message.includes('Token has been revoked')
      )) {
        console.error('🔒 [CONNECTION MONITOR] Authentication error - stopping reconnection attempts. Please refresh or login again.');
        // Clear any pending connection timeout
        if (this.connectionTimeoutId) {
          clearTimeout(this.connectionTimeoutId);
          this.connectionTimeoutId = null;
        }
        this.isReconnecting = false;
        this.reconnectAttempts = this.maxReconnectAttempts; // Mark as failed
        this.emit('reconnectFailed');
        this.startServerMonitoring(); // Just monitor, don't retry
        return;
      }

      // If we're already in a reconnection attempt, handle the error immediately
      if (this.isReconnecting) {
        console.log(`Reconnection attempt ${this.reconnectAttempts} failed - connection error: ${error.message}`);
        
        // Clear the connection timeout since we're handling the error now
        if (this.connectionTimeoutId) {
          clearTimeout(this.connectionTimeoutId);
          this.connectionTimeoutId = null;
        }
        
        // Clean up the failed socket
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
        
        // Try next attempt if we haven't reached max attempts
        // Keep isReconnecting = true so banner continues to show during all attempts
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          // Continue reconnecting - isReconnecting stays true for banner visibility
          this.attemptReconnection();
        } else {
          // All attempts failed - now set isReconnecting to false to show "Computer not connected"
          this.isReconnecting = false;
          // Emit reconnectFailed with status so UI can update properly
          this.emit('reconnectFailed', {
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: this.maxReconnectAttempts
          });
          // Start monitoring server availability (but don't auto-retry)
          this.startServerMonitoring();
        }
        return;
      }

      // Only attempt reconnection for certain error types (network/transport errors)
      // This handles initial disconnections when not already reconnecting
      if (
        error.message.includes('timeout') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ENOTFOUND') ||
        error.message.includes('WebSocket') ||
        error.type === 'TransportError'
      ) {
        console.log('🔄 Socket connection error, starting intelligent reconnection');
        this.attemptReconnection();
      }
    });

    // Handle connection timeout
    this.socket.on('connect_timeout', () => {
      console.error('⏱️ [CONNECTION MONITOR] Socket connection timeout after', this.connectionTimeout, 'ms');
      console.error('⏱️ [CONNECTION MONITOR] Timeout details:', {
        socketExists: !!this.socket,
        connected: this.socket?.connected,
        disconnected: this.socket?.disconnected,
        id: this.socket?.id,
        transport: this.socket?.io?.engine?.transport?.name,
        socketDomain: process.env.REACT_APP_WS_URL || process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000'
      });
      
      this.isConnected = false;
      
      // Notify listeners
      this.emit('disconnection');

      // Attempt reconnection
      if (!this.isReconnecting) {
        console.log('🔄 [CONNECTION MONITOR] Starting reconnection after timeout');
        this.attemptReconnection();
      }
    });

    // Handle general socket errors (including auth errors after connection)
    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
      
      // Check if it's an authentication error
      if (error && (
        error.message?.includes('Authentication token required') ||
        error.message?.includes('Token expired') ||
        error.message?.includes('Invalid token') ||
        error.message?.includes('Token has been revoked')
      )) {
        console.error('🔒 Socket authentication error - stopping reconnection attempts');
        this.isConnected = false;
        this.isReconnecting = false;
        this.reconnectAttempts = this.maxReconnectAttempts;
        this.emit('reconnectFailed');
        this.startServerMonitoring();
        this.emit('disconnection');
      }
    });

    // Copy all other event listeners from the original socket handler
    this.copyOriginalSocketListeners();
  }

  // Copy all the original socket event listeners
  copyOriginalSocketListeners() {
    if (!this.socket) return;

    // Import the original socket handler functions
    const originalSocketHandlers = {
      'receive_message': this.handleReceiveMessage.bind(this),
      'message_status_update': this.handleMessageStatusUpdate.bind(this),
      'new_inbox_message': this.handleNewInboxMessage.bind(this),
      'assignment_count_changed': this.handleAssignmentCountChanged.bind(this),
      'user_connected_update': this.handleUserConnectedUpdate.bind(this),
      'user_disconnected_update': this.handleUserDisconnectedUpdate.bind(this),
      'contact_labels_updated': this.handleContactLabelsUpdated.bind(this),
      'forced_disconnect': this.handleForcedDisconnect.bind(this),
      'connection_warning': this.handleConnectionWarning.bind(this),
      'inbox_message_reaction': this.handleMessageReaction.bind(this),
      'team_member:chat:message': this.handleTeamMemberChatMessage.bind(this),
      'team_member:chat:status_update': this.handleTeamMemberChatStatusUpdate.bind(this),
      'team_member:chat:inbox_update': this.handleTeamMemberChatInboxUpdate.bind(this),
      'task_chat:message': this.handleTaskChatMessage.bind(this),
      'task_chat:log': this.handleTaskChatLog.bind(this),
      'task_chat:message_status': this.handleTaskChatMessageStatus.bind(this),
      'task:updated': this.handleTaskUpdated.bind(this),
      'task:deleted': this.handleTaskDeleted.bind(this),
      // Calling — signaling only, no redux dispatch here. Just re-emitted on
      // this same EventEmitter (connectionMonitor already extends one) so
      // useCallConnection can subscribe with connectionMonitor.on(...) and
      // handle the raw SDP/state itself, the same way CallButton triggers an
      // outbound dial via connectionMonitor.emit('call:requestDial', ...).
      'call:incoming': (payload) => this.emit('call:incoming', payload),
      'call:status': (payload) => this.emit('call:status', payload),
      'call:answered': (payload) => this.emit('call:answered', payload),
      'call:ended': (payload) => this.emit('call:ended', payload),
      'call:claimed': (payload) => this.emit('call:claimed', payload),
      'call:ring_released': (payload) => this.emit('call:ring_released', payload),
      'call:log_updated': (payload) => this.emit('call:log_updated', payload),
      'call:live_update': (payload) => this.emit('call:live_update', payload),
      'call:transferRequest': (payload) => this.emit('call:transferRequest', payload),
      'call:transferCancelled': (payload) => this.emit('call:transferCancelled', payload),
      'call:transferDeclined': (payload) => this.emit('call:transferDeclined', payload),
      'call:transferCompleted': (payload) => this.emit('call:transferCompleted', payload),
      // QR (Baileys) WhatsApp connection — same signaling-only re-emit pattern as calling above,
      // so useWhatsAppQrSignup can subscribe with connectionMonitor.on('whatsapp_qr:code'/
      // 'whatsapp_qr:status', ...). Emitted server-side from whatsapp-worker's
      // baileysConnectionManager.js to the same workspace:<workspaceId> room this socket is
      // already joined to.
      // Inbox multi-select bulk Close/Assign job progress/result — sent only to the team member
      // who started the job (server/utils/socketHandler.js emitInboxBulkActionUpdate). Same
      // signaling-only re-emit; InboxBulkJobTracker.jsx subscribes with connectionMonitor.on(...).
      'inbox_bulk_action': (payload) => this.emit('inbox_bulk_action', payload),
      'whatsapp_qr:code': (payload) => this.emit('whatsapp_qr:code', payload),
      'whatsapp_qr:status': (payload) => this.emit('whatsapp_qr:status', payload),
      // Fired the moment a QR connection enters its every-100-messages burst-pause, so a
      // listener (InboxAll.jsx) can show a real-time toast instead of a send attempt just
      // silently hanging for up to 5 minutes with no explanation.
      'whatsapp_qr:burst_pause': (payload) => this.emit('whatsapp_qr:burst_pause', payload),
      // The rest after a burst is over for one number (sending is open again), and a scan refused by the number
      // rules (already linked / currently on Meta / not the number that was typed).
      'whatsapp_qr:burst_resume': (payload) => this.emit('whatsapp_qr:burst_resume', payload),
      'whatsapp_qr:rejected': (payload) => this.emit('whatsapp_qr:rejected', payload),
      // Fired at 80%/90%/100% of a QR connection's daily send cap (checkAndReportDailyLimit in
      // baileysConnectionManager.js) -- built in Phase 2 but never actually wired to a listener
      // until now, same gap whatsapp_qr:burst_pause had before it was fixed.
      'whatsapp_qr:daily_limit_alert': (payload) => this.emit('whatsapp_qr:daily_limit_alert', payload),
      // A WhatsApp group's stored record changed (members/settings/subject synced from WhatsApp);
      // InboxAll.jsx re-fetches the open group's info so the members box updates without a refresh.
      'group_updated': (payload) => this.emit('group_updated', payload),
      // We left / were removed from a WhatsApp group: InboxAll.jsx drops its card everywhere.
      'group_left': (payload) => this.emit('group_left', payload),
      // A Shopify customer sync just linked a contact to its Shopify customer (shopify:customer:linked,
      // emitted by shopifyOrderMessagingService.js). Re-emitted as-is; useShopifyCustomerLinkSync
      // (mounted by ChatInfo.jsx) patches the open contact so the Shopify icon appears without a reload.
      'shopify:customer:linked': (payload) => this.emit('shopify:customer:linked', payload),
    };

    // Set up all the event listeners
    console.log(`🔧 [CONNECTION MONITOR] Setting up socket event listeners`);
    Object.entries(originalSocketHandlers).forEach(([event, handler]) => {
      console.log(`📡 [CONNECTION MONITOR] Registering listener for event: ${event}`);
      this.socket.on(event, handler);
    });
    console.log(`✅ [CONNECTION MONITOR] All socket event listeners registered`);
  }

  // Socket event handlers (these will be populated by the main socket handler)
  handleReceiveMessage = (message) => {
    try {
      // This will be set by the main socket handler
      if (this.receiveMessageHandler) {
        this.receiveMessageHandler(message);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleReceiveMessage:`, error);
      // Errors are already handled within the handler functions
    }
  };

  handleMessageStatusUpdate = (statusData) => {
    try {
      if (this.messageStatusUpdateHandler) {
        this.messageStatusUpdateHandler(statusData);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleMessageReaction = (payload) => {
    try {
      if (this.messageReactionHandler) {
        this.messageReactionHandler(payload);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleMessageReaction:`, error);
    }
  };

  handleNewInboxMessage = (message) => {
    try {
      if (this.newInboxMessageHandler) {
        this.newInboxMessageHandler(message);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleAssignmentCountChanged = (data) => {
    try {
      if (this.assignmentCountChangedHandler) {
        this.assignmentCountChangedHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleUserConnectedUpdate = (data) => {
    try {
      if (this.userConnectedUpdateHandler) {
        this.userConnectedUpdateHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleUserDisconnectedUpdate = (data) => {
    try {
      if (this.userDisconnectedUpdateHandler) {
        this.userDisconnectedUpdateHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleContactLabelsUpdated = (data) => {
    try {
      if (this.contactLabelsUpdatedHandler) {
        this.contactLabelsUpdatedHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleForcedDisconnect = (data) => {
    try {
      if (this.forcedDisconnectHandler) {
        this.forcedDisconnectHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  handleConnectionWarning = (data) => {
    try {
      if (this.connectionWarningHandler) {
        this.connectionWarningHandler(data);
      }
    } catch (error) {
      // Silently catch all errors to prevent them from showing to users
      // Errors are already handled within the handler functions
    }
  };

  // Setter methods for event handlers
  setReceiveMessageHandler(handler) {
    console.log(`🔧 [CONNECTION MONITOR] Setting receiveMessageHandler:`, {
      hasHandler: !!handler,
      handlerType: typeof handler
    });
    this.receiveMessageHandler = handler;
    console.log(`✅ [CONNECTION MONITOR] receiveMessageHandler set successfully`);
  }

  setMessageStatusUpdateHandler(handler) {
    this.messageStatusUpdateHandler = handler;
  }

  setNewInboxMessageHandler(handler) {
    this.newInboxMessageHandler = handler;
  }

  setAssignmentCountChangedHandler(handler) {
    this.assignmentCountChangedHandler = handler;
  }

  setMessageReactionHandler(handler) {
    this.messageReactionHandler = handler;
  }

  setUserConnectedUpdateHandler(handler) {
    this.userConnectedUpdateHandler = handler;
  }

  setUserDisconnectedUpdateHandler(handler) {
    this.userDisconnectedUpdateHandler = handler;
  }

  setContactLabelsUpdatedHandler(handler) {
    this.contactLabelsUpdatedHandler = handler;
  }

  setForcedDisconnectHandler(handler) {
    this.forcedDisconnectHandler = handler;
  }

  setConnectionWarningHandler(handler) {
    this.connectionWarningHandler = handler;
  }

  setTeamMemberChatMessageHandler(handler) {
    this.teamMemberChatMessageHandler = handler;
  }

  setTeamMemberChatStatusUpdateHandler(handler) {
    this.teamMemberChatStatusUpdateHandler = handler;
  }

  setTeamMemberChatInboxUpdateHandler(handler) {
    this.teamMemberChatInboxUpdateHandler = handler;
  }

  setTaskChatMessageHandler(handler) {
    this.taskChatMessageHandler = handler;
  }

  setTaskChatLogHandler(handler) {
    this.taskChatLogHandler = handler;
  }

  setTaskChatMessageStatusHandler(handler) {
    this.taskChatMessageStatusHandler = handler;
  }

  setTaskUpdatedHandler(handler) {
    this.taskUpdatedHandler = handler;
  }

  setTaskDeletedHandler(handler) {
    this.taskDeletedHandler = handler;
  }

  handleTaskChatMessage = (data) => {
    try {
      if (this.taskChatMessageHandler) {
        this.taskChatMessageHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTaskChatMessage:`, error);
    }
  };

  handleTaskChatLog = (data) => {
    try {
      if (this.taskChatLogHandler) {
        this.taskChatLogHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTaskChatLog:`, error);
    }
  };

  handleTaskChatMessageStatus = (data) => {
    try {
      if (this.taskChatMessageStatusHandler) {
        this.taskChatMessageStatusHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTaskChatMessageStatus:`, error);
    }
  };

  handleTaskUpdated = (data) => {
    try {
      if (this.taskUpdatedHandler) {
        this.taskUpdatedHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTaskUpdated:`, error);
    }
  };

  handleTaskDeleted = (data) => {
    try {
      if (this.taskDeletedHandler) {
        this.taskDeletedHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTaskDeleted:`, error);
    }
  };

  handleTeamMemberChatMessage = (data) => {
    try {
      if (this.teamMemberChatMessageHandler) {
        this.teamMemberChatMessageHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTeamMemberChatMessage:`, error);
    }
  };

  handleTeamMemberChatStatusUpdate = (data) => {
    try {
      if (this.teamMemberChatStatusUpdateHandler) {
        this.teamMemberChatStatusUpdateHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTeamMemberChatStatusUpdate:`, error);
    }
  };

  handleTeamMemberChatInboxUpdate = (data) => {
    try {
      if (this.teamMemberChatInboxUpdateHandler) {
        this.teamMemberChatInboxUpdateHandler(data);
      }
    } catch (error) {
      console.error(`❌ [CONNECTION MONITOR] Error in handleTeamMemberChatInboxUpdate:`, error);
    }
  };

  // Attempt reconnection with intelligent retry logic
  async attemptReconnection() {
    // Check if we've exceeded max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached. Connection failed.');
        this.isReconnecting = false;
        // Emit reconnectFailed with status so UI can update properly
        this.emit('reconnectFailed', {
          reconnectAttempts: this.reconnectAttempts,
          maxReconnectAttempts: this.maxReconnectAttempts
        });
        // Start monitoring for server availability
        this.startServerMonitoring();
        return;
    }

    // Only diagnose the issue on the first attempt to avoid repeated health checks
    if (this.reconnectAttempts === 0) {
      this.diagnosedIssueType = await this.diagnoseConnectionIssue();
      console.log(`🔍 Diagnosed connection issue: ${this.diagnosedIssueType}`);
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    // Notify listeners as each attempt starts (not just on final failure) so
    // UI banners can show a live "Reconnecting... X/Y" counter.
    this.emit('reconnecting', {
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    });

    console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    
    // Adjust timing based on issue type (use stored value from first diagnosis)
    const baseDelay = this.getReconnectionDelay(this.diagnosedIssueType || 'server_down');
    // Add random jitter (0-40% of base) so clients disconnected by the same event
    // (socket-server restart/blip) don't all retry at the exact same instant —
    // socket-server is a single unclustered process and can't absorb a synchronized
    // reconnect burst from every connected client at once.
    const delay = Math.round(baseDelay + baseDelay * 0.4 * Math.random());
    console.log(`⚡ Intelligent timing: ${delay/1000}s delay, ${this.connectionTimeout/1000}s timeout per attempt`);
    console.log(`📊 Total estimated time: ${(delay + this.connectionTimeout)/1000}s`);

    this.reconnectTimeout = setTimeout(() => {
      this.performReconnection();
    }, delay);
  }

  // Get reconnection delay based on issue type
  getReconnectionDelay(issueType) {
    switch (issueType) {
      case 'socket_issue':
        return 1000; // 1 second for socket issues (server is running)
      case 'server_down':
        return 3000; // 3 seconds for server down
      case 'internet_down':
        return 5000; // 5 seconds for internet down
      default:
        return 2000; // 2 seconds default
    }
  }

  async performReconnection() {
    try {
      // Disconnect the existing socket if it exists
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      // Get access token for authentication. Try memory first, then fall back to
      // refreshing from the httpOnly cookie (shared, de-duped implementation) —
      // OS sleep reliably drops the socket, and by the time we reconnect the
      // in-memory token has often already expired. Without this fallback,
      // reconnection used to give up immediately instead of self-healing like
      // REST calls already do.
      const { getAuthToken } = require('./getAuthToken');
      const accessToken = await getAuthToken();

      if (!accessToken) {
        console.error('No access token available for socket reconnection');
        this.isReconnecting = false;
        this.emit('reconnectFailed');
        this.startServerMonitoring();
        return;
      }

      // Create a new socket connection with authentication (same socket-server
      // URL resolution as the initial connect — see comment there).
      const serverDomain = process.env.REACT_APP_WS_URL || process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

      // Get workspaceId from Redux state (not localStorage for security)
      let workspaceId = null;
      try {
        const { store } = require('../redux/store');
        workspaceId = store.getState()?.auth?.user?.workspaceId || null;
      } catch (error) {
        console.warn('Failed to get workspaceId from Redux:', error);
      }
      
      // Use the accessToken already retrieved above (line 671)
      const token = accessToken;
      
      this.socket = io(serverDomain, {
        autoConnect: true,
        reconnection: false, // We handle reconnection manually
        timeout: this.connectionTimeout,
        transports: ['websocket', 'polling'],
        upgrade: true,
        rememberUpgrade: true,
        forceNew: true, // Force new connection to ensure fresh auth
        auth: {
          token: token, // Send JWT token for authentication
        },
        query: {
          token: token, // Fallback: also send in query
          workspaceId: workspaceId || undefined
        },
      });

      // Set up event listeners for the new socket
      this.setupSocketListeners();

      // Connect the socket
      this.socket.connect();

      // Set up a timeout to check if connection succeeded
      // Store timeout ID so we can clear it if connect_error fires first
      this.connectionTimeoutId = setTimeout(() => {
        // Only proceed if we're still trying to reconnect and not connected
        if (!this.isConnected && this.isReconnecting) {
          console.log(`Reconnection attempt ${this.reconnectAttempts} failed - no connection established within ${this.connectionTimeout/1000}s`);
          
          // Clear the timeout ID
          this.connectionTimeoutId = null;
          
          // Clean up the failed socket
          if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
          }
          
          // Try next attempt if we haven't reached max attempts
          // Keep isReconnecting = true so banner continues to show during all attempts
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            // Continue reconnecting - isReconnecting stays true for banner visibility
            this.attemptReconnection();
          } else {
            // All attempts failed - now set isReconnecting to false to show "Computer not connected"
            this.isReconnecting = false;
            // Emit reconnectFailed with status so UI can update properly
            this.emit('reconnectFailed', {
              reconnectAttempts: this.reconnectAttempts,
              maxReconnectAttempts: this.maxReconnectAttempts
            });
            // Start monitoring server availability (but don't auto-retry)
            this.startServerMonitoring();
          }
        }
      }, this.connectionTimeout);

      // Note: connectionTimeoutId is already cleared inside setupSocketListeners()'s
      // own 'connect' handler (see above) — registering a second 'connect' listener
      // here that manually re-invoked the first one used to cause user_connected to
      // be emitted twice per reconnect (the first listener runs automatically, then
      // this second one called it again), which cascaded into spurious self-eviction
      // once the session cap was enforced. Do not re-add a second listener.

    } catch (error) {
      console.error('Error during reconnection:', error);
      
      // Clear connection timeout if it exists
      if (this.connectionTimeoutId) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }
      
      // Clean up the failed socket
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }
      
      // Try next attempt if we haven't reached max attempts
      // Keep isReconnecting = true so banner continues to show during all attempts
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        // Continue reconnecting - isReconnecting stays true for banner visibility
        this.attemptReconnection();
      } else {
        // All attempts failed - now set isReconnecting to false to show "Computer not connected"
        this.isReconnecting = false;
        // Emit reconnectFailed with status so UI can update properly
        this.emit('reconnectFailed', {
          reconnectAttempts: this.reconnectAttempts,
          maxReconnectAttempts: this.maxReconnectAttempts
        });
        // Start monitoring server availability
        this.startServerMonitoring();
      }
    }
  }

  // Manual disconnect
  disconnect() {
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }

    if (this.initialConnectionTimeout) {
      clearTimeout(this.initialConnectionTimeout);
      this.initialConnectionTimeout = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Get socket instance
  getSocket() {
    return this.socket;
  }

  // Get connection status
  getConnectionStatus() {
    // Always return current state, even if socket is null (might be cleaned up during reconnection)
    // This ensures banner stays visible during reconnection attempts
    return {
      isConnected: this.isConnected,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }

  // Reset reconnection attempts (useful for manual retry)
  resetReconnectionAttempts() {
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.initialConnectionTimeout) {
      clearTimeout(this.initialConnectionTimeout);
      this.initialConnectionTimeout = null;
    }
  }

  // Manual retry connection
  retryConnection() {
    console.log('Manual retry requested');
    this.resetReconnectionAttempts();
    this.attemptReconnection();
  }

  // Method to check if the socket-server (not the API server) is reachable —
  // this is what the socket actually connects to, so it's the relevant thing
  // to probe when diagnosing a socket connection failure.
  async checkServerAvailability() {
    try {
      const socketDomain = process.env.REACT_APP_WS_URL || process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
      const response = await fetch(`${socketDomain}/health`, {
        method: 'GET',
        cache: 'no-cache'
      });
      // Check if response is ok (status 200-299)
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  // Method to check if it's a network issue vs server issue
  async diagnoseConnectionIssue() {
    const serverAvailable = await this.checkServerAvailability();
    const internetAvailable = navigator.onLine;
    
    if (!internetAvailable) {
      return 'internet_down';
    } else if (!serverAvailable) {
      return 'server_down';
    } else {
      return 'socket_issue';
    }
  }

  // Method to start periodic server checking
  startServerMonitoring() {
    // Stop any existing monitoring first
    this.stopServerMonitoring();
    
    // Only monitor silently - no logs to avoid console spam
    // Health API was already called during reconnection attempts
    // This monitoring is just to detect when connection is restored
    this.serverCheckInterval = setInterval(() => {
      if (!this.isConnected && !this.isReconnecting) {
        // Silently monitor - no console logs to avoid spam
        // User already sees "Computer not connected" banner
      } else if (this.isConnected) {
        // If we're connected, stop monitoring
        this.stopServerMonitoring();
      }
    }, 30000); // Check every 30 seconds (silent monitoring, no logs)
  }

  // Method to stop server monitoring
  stopServerMonitoring() {
    if (this.serverCheckInterval) {
      clearInterval(this.serverCheckInterval);
      this.serverCheckInterval = null;
    }
  }

  // Get detailed connection info for debugging
  getConnectionInfo() {
    return {
      socketExists: !!this.socket,
      socketConnected: this.socket?.connected || false,
      isConnected: this.isConnected,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      reconnectDelay: this.reconnectDelay,
      connectionTimeout: this.connectionTimeout,
      transport: this.socket?.io?.engine?.transport?.name || 'unknown',
      readyState: this.socket?.io?.engine?.readyState || 'unknown',
      // Optimal timing statistics
      totalEstimatedTime: (this.reconnectDelay * this.maxReconnectAttempts + this.connectionTimeout * this.maxReconnectAttempts) / 1000,
      currentAttemptDelay: this.reconnectDelay * this.reconnectAttempts,
    };
  }
}

// Create a singleton instance
const connectionMonitor = new ConnectionMonitor();

export default connectionMonitor;